//GlobalContextx.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react"

const GlobalContext = createContext()

/**
 * Normalize any recognition payload so jsonResults always stores only `result` items.
 * Accepts: { result: [...] }, [...words], [ { result: [...] }, ... ], [[...words], ...]
 */
export const extractJsonResultItems = (payload) => {
  if (payload == null) return []

  if (typeof payload === "object" && !Array.isArray(payload) && "result" in payload) {
    return extractJsonResultItems(payload.result)
  }

  if (!Array.isArray(payload)) {
    return [payload]
  }

  if (payload.length === 0) return []

  // Array of wrappers that each have a result key
  if (payload.every((item) => item && typeof item === "object" && !Array.isArray(item) && "result" in item)) {
    return payload.flatMap((item) => extractJsonResultItems(item.result))
  }

  // Array of result arrays (e.g. previously stored chunks of data.result)
  if (payload.every((item) => Array.isArray(item))) {
    return payload.flatMap((item) => extractJsonResultItems(item))
  }

  // Already a flat list of result items (e.g. [{ word: "..." }, ...])
  return payload
}

/** Words whose [start, end) interval (ms) covers the playback time. */
export const findJsonResultsAtTimeMs = (jsonResults, timeMs) => {
  if (!Array.isArray(jsonResults) || jsonResults.length === 0) return []
  const t = Number(timeMs)
  if (!Number.isFinite(t)) return []
  return jsonResults.filter((item) => {
    const start = Number(item?.start)
    const end = Number(item?.end)
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false
    return t >= start && t < end
  })
}

export const GlobalProvider = ({ children }) => {
  const [backToUserManagement, setBackToUserManagement] = useState(false)
  const [adminWatch, setAdminWatch] = useState(false)
  // When true: document opened from All Documents — playback only, no edits/dictate/save
  const [documentReadOnly, setDocumentReadOnly] = useState(false)
  // True when the open doc must not receive dictation: read-only, or workflow doc of another author (e.g. TS on assigned doc)
  const [dictationBlocked, setDictationBlocked] = useState(false)
  // Full doc payload from admin getDocumentById; VdGrid opens it via handleRowClick
  const [openAdminDocumentAction, setOpenAdminDocumentAction] = useState(null)
  const [userRole, setUserRole] = useState(false)
  const [userExpert, setUserExpert] = useState(() =>
    Number(localStorage.getItem("expert_mode")) === 1 ? 1 : 0
  )
  // Author/Transcriptionist are assignable; legacy Reviewer rows remain for existing document workflows.
  const [workflowRoles, setWorkflowRoles] = useState(() => {
    try {
      const raw = localStorage.getItem("workflowRoles")
      return raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
  })
  const setWorkflowRolesPersisted = (roles) => {
    const next = Array.isArray(roles) ? roles : []
    setWorkflowRoles(next)
    localStorage.setItem("workflowRoles", JSON.stringify(next))
  }
  const [isRecording, setIsRecording] = useState(false)
  const [isCheckingServerMessages, setIsCheckingServerMessages] = useState(false)
  const [isRecordingRef, setIsRecordingRef] = useState(false)
  
  // Initialize selectedTopic from localStorage
  const [selectedTopic, setSelectedTopicState] = useState(() => {
    try {
      const saved = localStorage.getItem("selectedTopic")
      return saved ? JSON.parse(saved) : null
    } catch (e) {
      console.warn("Failed to parse selectedTopic from localStorage:", e)
      return null
    }
  })
  
  // Wrapper to save to localStorage when topic changes
  const setSelectedTopic = (topic) => {
    setSelectedTopicState(topic)
    if (topic) {
      localStorage.setItem("selectedTopic", JSON.stringify(topic))
    } else {
      localStorage.removeItem("selectedTopic")
    }
  }
  
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [dictateAction, setDictateAction] = useState(null) // 'start' or 'stop'
  const [showEditor, setShowEditor] = useState(true)
  const [openDocumentAction, setOpenDocumentAction] = useState(false)
  const [newDocumentAction, setNewDocumentAction] = useState(false)
  const [replayRecognitionAction, setReplayRecognitionAction] = useState(false)
  const [audioDuration, setAudioDuration] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  // Editor top strip: true = ReplayDock (audio player), false = Live recognition preview.
  // Together with selectedDoc, survives any menu route change / VdGrid remount.
  // New Document is the only intentional full reset of the editor session.
  const [isReplaying, setIsReplaying] = useState(false)
  const [saveDocumentAction, setSaveDocumentAction] = useState(false)
  const [documentDirty, setDocumentDirty] = useState(false)
  const pendingNavigationRef = useRef(null)
  const [gVdssSessionId, setGvdssSessionId] = useState(null)
  const [isWsPreinitLoading, setIsWsPreinitLoading] = useState(false)
  // Flat list of recognition result items (always the contents of a `result` key, never wrappers)
  const [jsonResults, setJsonResults] = useState([])

  useEffect(() => {
    console.log("[jsonResults] changed", {
      length: Array.isArray(jsonResults) ? jsonResults.length : 0,
      content: jsonResults,
    })
  }, [jsonResults])

  // Menu states
  const [activeMenuItem, setActiveMenuItem] = useState("editorView")
  // Bumps when workflow badge counts/revision change (Menu poll) — Open Document list listens
  const [workflowBadgeRevision, setWorkflowBadgeRevision] = useState("")
  
  // Menu components array with visibility states
  const [menuComponents, setMenuComponents] = useState([
    { id: "editorView", visible: true },
    { id: "voiceCommands", visible: false },
    { id: "newDocument", visible: false },
    { id: "openDocument", visible: false },
    { id: "assignedDocuments", visible: false },
    { id: "saveDocument", visible: false },
    { id: "changeTopic", visible: false },
    { id: "topicEditor", visible: false },
    { id: "dictationLog", visible: false },
    { id: "userManagement", visible: false },
    { id: "adminFunctions", visible: false },
    { id: "allDocuments", visible: false },
    { id: "i18n", visible: false },
    { id: "userProfile", visible: false },
  ])

  // Function to set active menu item - hides all, then shows the specified one
  const setActiveMenu = (menuId) => {
    setMenuComponents(prev => 
      prev.map(comp => ({
        ...comp,
        visible: comp.id === menuId
      }))
    )
    setActiveMenuItem(menuId)
  }

  //const [SSL, setSSL] = useState("")

  return (
    <GlobalContext.Provider value={{
      backToUserManagement, setBackToUserManagement,
      adminWatch, setAdminWatch,
      documentReadOnly, setDocumentReadOnly,
      dictationBlocked, setDictationBlocked,
      openAdminDocumentAction, setOpenAdminDocumentAction,
      userRole, setUserRole,
      userExpert, setUserExpert,
      workflowRoles, setWorkflowRoles: setWorkflowRolesPersisted,
      isRecording, setIsRecording,
      isCheckingServerMessages, setIsCheckingServerMessages,
      isRecordingRef, setIsRecordingRef,
      selectedTopic, setSelectedTopic,
      selectedDoc, setSelectedDoc,
      dictateAction, setDictateAction,
      showEditor, setShowEditor,
      openDocumentAction, setOpenDocumentAction,
      newDocumentAction, setNewDocumentAction,
      replayRecognitionAction, setReplayRecognitionAction,
      audioDuration, setAudioDuration,
      isPlaying, setIsPlaying,
      isReplaying, setIsReplaying,
      saveDocumentAction, setSaveDocumentAction,
      documentDirty, setDocumentDirty,
      pendingNavigationRef,
      activeMenuItem, setActiveMenuItem,
      workflowBadgeRevision, setWorkflowBadgeRevision,
      menuComponents, setMenuComponents,
      setActiveMenu,
      gVdssSessionId, setGvdssSessionId,
      isWsPreinitLoading, setIsWsPreinitLoading,
      jsonResults, setJsonResults,
      //SSL, setSSL,
    }}>
      {children}
    </GlobalContext.Provider>
  )
}

export const useGlobal = () => useContext(GlobalContext)
